import { useEffect, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useSessionStore } from "stores/session";

import SearchParams from "simplydo/core/searchParams";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Divider } from "simplydo/react/ui/divider";
import { H2, Subheader } from "simplydo/react/ui/header";
import { Input } from "simplydo/react/ui/input";
import { Message } from "simplydo/react/ui/message";

import api from "api";
import constants from "utils/constants";

import TermsCheckbox from "components/lib/Entry/TermsCheckbox";

function Enrol() {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [invalidToken, setInvalidToken] = useState(false);
  const [loading, setLoading] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");

  const organisation = useSessionStore((state) => state.urlOrganisation);

  const [agreedCustomTerms, setAgreedCustomTerms] = useState(
    !organisation?.customTerms?.enabled,
  );
  const navigate = useNavigate();

  useEffect(() => {
    const params = new SearchParams(window.location.search);
    const token = params.get("token");
    if (!token) return;
    let isMounted = true;
    api.auth.getPasswordTokenDetails(
      token,
      (data) => {
        if (!isMounted) return;
        setEmail(data.email);
        setFirstName(data.firstName ?? "");
      },
      () => {
        if (!isMounted) return;
        toast.error("Invalid enrolment token");
        setInvalidToken(true);
      },
    );
    return () => {
      isMounted = false;
    };
  }, [setEmail]);

  function completeSetPassword(callback: () => void) {
    const params = new SearchParams(window.location.search);
    const token = params.get("token");
    if (!token) {
      setError("Invalid enrolment token");
      return;
    }
    setLoading(true);
    setError("");
    api.auth.updatePasswordWithToken(
      token,
      password,
      () => {
        if (email) {
          api.auth.login(
            { email, password },
            () => {
              navigate("/");
              setTimeout(() => {
                callback();
              }, 50);
            },
            (err) => {
              setError(err.message);
              setLoading(false);
            },
          );
        } else {
          setLoading(false);
          navigate("/");
          toast(t("entry.enrol.passwordSuccess"));
        }
      },
      (err) => {
        setLoading(false);
        setError(err.message);
      },
    );
  }

  function agreeTerms() {
    setError("");
    api.auth.agreeToTerms(
      constants.termsVersion,
      organisation?.customTerms?.version ?? "",
      () => {
        setAgreedTerms(true);
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
  }

  function clicked() {
    if (!agreedTerms) return setError(t("entry.enrol.agreeToTerms"));
    completeSetPassword(agreeTerms);
  }

  const welcomeTitle = firstName
    ? t("entry.enrol.welcome_name", { firstName })
    : t("entry.enrol.welcome");

  return (
    <div>
      {invalidToken ? (
        <>
          <H2>A problem occurred</H2>
          <Message color="red">
            Your enrolment link is invalid or has expired. To receive a new
            enrolment link, try logging in again. Please get in touch with us if
            this issue persists.
            <br />
            <div style={{ marginTop: 15 }}>
              <ButtonLink color="primary" to="/login">
                Return to Login
              </ButtonLink>
            </div>
          </Message>
        </>
      ) : (
        <>
          <H2>
            {welcomeTitle}
            <Subheader>{t("entry.enrol.info")}</Subheader>
          </H2>
          <Divider hidden />
          {error && <Message color="orange">{error}</Message>}
          <Input
            style={{ minHeight: "40px" }}
            fluid
            type="password"
            placeholder={t("entry.enrol.newPass")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />
          <Divider hidden />
          <TermsCheckbox
            organisation={organisation}
            agreed={agreedTerms}
            onAgree={(c) => setAgreedTerms(c)}
            agreedCustomTerms={agreedCustomTerms}
            onAgreeCustomTerms={(c) => setAgreedCustomTerms(c)}
          />
          <div style={{ marginTop: 30, textAlign: "right" }}>
            <Button onClick={clicked} loading={loading} color="primary">
              {t("entry.enrol.complete")}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default Enrol;
